import { H4, Label, useFormatDate } from "@dgs/core";
import { parseISO } from "date-fns";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RegistrationShopSummaryResource } from "../../../shared/types";
import { PaymentProviderSelect } from "./PaymentProviderSelect";
import { PaymentTransactionItemRow } from "./PaymentTransactionItemRow";
import {
	GreyBox,
	RegistrationShopSummarySection,
	Separator,
	Subheading,
	SummaryAccordionItem,
} from "./ShopSummaryComponents";
import { TaxDisclaimers } from "./TaxDisclaimers";
import { TotalPrices } from "./TotalPrices";

interface Props {
	shopSummaryResource: RegistrationShopSummaryResource;
}

export const RegistrationShopSummary = ({ shopSummaryResource }: Props) => {
	const { t } = useTranslation();
	const formatDate = useFormatDate();

	const activePaymentItems = useMemo(() => {
		return shopSummaryResource
			? shopSummaryResource.transactionItemsToBePaidNow.concat(shopSummaryResource.transactionItemsToBeRefunded)
			: [];
	}, [shopSummaryResource]);

	const toBePaidTaxes = (shopSummaryResource?.toBePaidTaxes ?? []).map((t, index) => ({ ...t, index: index + 1 }));

	return (
		<RegistrationShopSummarySection>
			<H4>{t("Payment Summary")}</H4>
			{shopSummaryResource.transactionItemsToBePaidNow.length > 0 && (
				<>
					<Subheading>{t("To be paid")}</Subheading>
					<GreyBox>
						{shopSummaryResource.transactionItemsToBePaidNow.map((item, index) => (
							<Fragment key={item.id ?? index}>
								<PaymentTransactionItemRow item={item} taxes={toBePaidTaxes} />
								{index + 1 < shopSummaryResource.transactionItemsToBePaidNow.length && <Separator />}
							</Fragment>
						))}
					</GreyBox>
				</>
			)}
			{shopSummaryResource.transactionItemsToBeRefunded.length > 0 && (
				<>
					<Subheading>{t("To be refunded")}</Subheading>
					<GreyBox>
						{shopSummaryResource.transactionItemsToBeRefunded.map((item, index) => (
							<Fragment key={item.id ?? index}>
								<PaymentTransactionItemRow
									item={item}
									taxes={toBePaidTaxes}
									labels={
										item.paidAt && (
											<Label size="small">
												{t("Paid on {{date}}", {
													date: formatDate(parseISO(item.paidAt)),
												})}
											</Label>
										)
									}
								/>
								{index + 1 < shopSummaryResource.transactionItemsToBeRefunded.length && <Separator />}
							</Fragment>
						))}
					</GreyBox>
				</>
			)}
			{shopSummaryResource.transactionItemsToBePaidLater.length > 0 && (
				<>
					<Subheading>{t("To be paid later")}</Subheading>
					<GreyBox>
						{shopSummaryResource.transactionItemsToBePaidLater.map((item, index) => (
							<Fragment key={item.id ?? index}>
								<PaymentTransactionItemRow
									item={item}
									taxes={toBePaidTaxes}
									labels={
										item.dueDate && (
											<Label size="small">
												{t("Due on {{date}}", {
													date: formatDate(parseISO(item.dueDate)),
												})}
											</Label>
										)
									}
								/>
								{index + 1 < shopSummaryResource.transactionItemsToBePaidLater.length && <Separator />}
							</Fragment>
						))}
					</GreyBox>
				</>
			)}
			{shopSummaryResource.transactionItemsAlreadyProcessed.length > 0 && (
				<SummaryAccordionItem
					title={t("Already processed")}
					body={
						<GreyBox>
							{shopSummaryResource.transactionItemsAlreadyProcessed.map((item, index) => (
								<Fragment key={item.id ?? index}>
									<PaymentTransactionItemRow
										item={item}
										taxes={toBePaidTaxes}
										labels={
											item.paidAt && (
												<Label size="small" color={!item.refundTotalPrice ? "success" : "danger"}>
													{t(!item.refundTotalPrice ? "Paid on {{date}}" : "Refunded on {{date}}", {
														date: formatDate(parseISO(item.paidAt)),
													})}
												</Label>
											)
										}
									/>
									{index + 1 < shopSummaryResource.transactionItemsAlreadyProcessed.length && <Separator />}
								</Fragment>
							))}
						</GreyBox>
					}
				></SummaryAccordionItem>
			)}
			{activePaymentItems.length > 0 && (
				<>
					<TaxDisclaimers taxes={shopSummaryResource.toBePaidTaxes} />
					<TotalPrices
						prices={shopSummaryResource}
						tobePaidTaxes={shopSummaryResource.toBePaidTaxes}
						totalTaxes={shopSummaryResource.totalTaxes}
						currencyType={activePaymentItems[0].currencyType}
					/>
				</>
			)}
			{shopSummaryResource.transactionItemsToBePaidNow.length > 0 && (
				<PaymentProviderSelect paymentProviders={shopSummaryResource.paymentProviders} />
			)}
		</RegistrationShopSummarySection>
	);
};
